import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const colors = [
  { key: "black", label: "Midnight Black", img: "/products/hero-black.jpg", glow: "rgba(120,140,180,0.35)", dot: "#1c1c1e" },
  { key: "lightblue", label: "Sky Blue", img: "/products/hero-lightblue.jpg", glow: "rgba(120,175,230,0.40)", dot: "#a9c7e8" },
  { key: "pink", label: "Blush Pink", img: "/products/hero-pink.jpg", glow: "rgba(230,150,180,0.38)", dot: "#f0b6c6" },
  { key: "darkblue", label: "Cobalt Blue", img: "/products/hero-darkblue.jpg", glow: "rgba(80,120,220,0.42)", dot: "#2b4a8b" },
];

export const CinematicHero = () => {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);

  const next = useCallback(() => setIndex((i) => (i + 1) % colors.length), []);

  useEffect(() => {
    const t = setInterval(next, 3800);
    return () => clearInterval(t);
  }, [next]);

  const active = colors[index];

  return (
    <section
      data-testid="cinematic-hero"
      className="relative w-full h-[92vh] min-h-[640px] overflow-hidden bg-[#050506]"
    >
      {/* Shifting color glow */}
      <div
        className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[900px] max-h-[900px] rounded-full blur-[120px] transition-colors duration-[1500ms] ease-out pointer-events-none"
        style={{ background: `radial-gradient(circle, ${active.glow} 0%, transparent 65%)` }}
      />

      {/* Product film — shared float animation, crossfading colors */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="hero-float relative w-full h-full max-w-5xl">
          {colors.map((c, i) => (
            <img
              key={c.key}
              src={c.img}
              alt={`Vsense glasses in ${c.label}`}
              aria-hidden={i !== index}
              className="absolute inset-0 w-full h-full object-contain transition-opacity duration-[1400ms] ease-out drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)]"
              style={{ opacity: i === index ? 1 : 0 }}
            />
          ))}
        </div>
      </div>

      {/* Scrims */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050506] via-transparent to-[#050506]/60 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050506]/70 via-transparent to-transparent pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col">
        {/* Bottom content */}
        <div className="mt-auto pb-14 md:pb-20 text-center flex flex-col items-center">
          <h1 className="font-display text-4xl sm:text-6xl md:text-7xl font-extrabold tracking-tighter text-white leading-[0.95] drop-shadow-[0_4px_40px_rgba(0,0,0,0.6)]">
            See more.<br className="sm:hidden" /> Worry less.
          </h1>
          <p className="mt-5 text-base md:text-xl text-white/70 max-w-xl leading-relaxed">
            Designed to keep the people you love connected and protected — in four colors, one beautiful frame.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex items-center justify-center gap-4">
            <button
              data-testid="cinematic-hero-reserve"
              onClick={() => navigate("/shop")}
              className="group inline-flex items-center gap-2 bg-white text-zinc-900 text-sm md:text-base font-semibold px-8 py-3.5 rounded-full hover:bg-zinc-200 active:scale-[0.98] transition-all duration-200"
            >
              Join VIP Access
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
            </button>
            <button
              data-testid="cinematic-hero-app"
              onClick={() => navigate("/app")}
              className="inline-flex items-center border border-white/40 text-white text-sm md:text-base font-medium px-8 py-3.5 rounded-full hover:bg-white hover:text-zinc-900 transition-colors duration-200 backdrop-blur-sm"
            >
              Explore the app
            </button>
          </div>

          {/* Color indicator */}
          <div className="mt-10 flex items-center gap-3">
            {colors.map((c, i) => (
              <button
                key={c.key}
                data-testid={`cinematic-hero-color-${c.key}`}
                onClick={() => setIndex(i)}
                aria-label={c.label}
                title={c.label}
                className="rounded-full transition-all duration-300"
                style={{
                  width: i === index ? 30 : 12,
                  height: 12,
                  backgroundColor: i === index ? "#ffffff" : c.dot,
                  boxShadow: i === index ? "0 0 0 2px rgba(255,255,255,0.25)" : "0 0 0 1px rgba(255,255,255,0.2)",
                }}
              />
            ))}
          </div>
          <p className="mt-3 text-xs text-white/40" data-testid="cinematic-hero-color-label">{active.label}</p>
        </div>
      </div>
    </section>
  );
};
